import { parseCsvCandles, V1_SYMBOLS, type AssetType, type DataInterval, type WatchSymbol } from "@tradesignal/core";
import { watchlist as mockWatchlist } from "../data/mock";
import { supabase } from "./supabase";

const WATCHLIST_KEY = "tradesignal.watchlist.v1";
const MANUAL_CSV_PREFIX = "tradesignal.manualCsv.v1.";
const v1Symbols = new Set<string>(V1_SYMBOLS);

interface WatchlistRow {
  id: string;
  symbol: string;
  asset_type: AssetType;
  timeframe: WatchSymbol["timeframe"];
  enabled: boolean;
  notifications_enabled: boolean;
}

export async function listWatchlist(): Promise<WatchSymbol[]> {
  if (!supabase) return readLocalWatchlist();
  const { data, error } = await supabase
    .from("watchlist")
    .select("id,symbol,asset_type,timeframe,enabled,notifications_enabled")
    .order("created_at", { ascending: true });
  if (error || !data?.length) return readLocalWatchlist();
  return mergeSavedWatchlist(data.map(watchlistFromRow).filter(isV1WatchSymbol));
}

export async function saveWatchSymbol(item: WatchSymbol): Promise<WatchSymbol> {
  if (!isV1WatchSymbol(item)) throw new Error("V1 only supports AMD, NVDA, TSLA, AAPL, MSFT, SPY, and QQQ.");
  if (!supabase) {
    const next = upsertWatchSymbol(readLocalWatchlist(), item);
    writeLocalWatchlist(next);
    return item;
  }

  const user = await supabase.auth.getUser();
  const userId = user.data.user?.id;
  if (!userId) throw new Error("Sign in before saving watchlist changes.");
  const row = {
    ...(isUuid(item.id) ? { id: item.id } : {}),
    user_id: userId,
    symbol: item.symbol,
    asset_type: item.assetType,
    timeframe: item.timeframe,
    enabled: item.enabled,
    notifications_enabled: item.notificationsEnabled
  };
  const { error } = await supabase.from("watchlist").upsert(row, { onConflict: "user_id,symbol" });
  if (error) throw error;
  return item;
}

export async function importManualCsvData({
  symbol,
  csv,
  interval = "1day"
}: {
  symbol: string;
  csv: string;
  interval?: DataInterval;
}) {
  const normalizedSymbol = symbol.trim().toUpperCase();
  if (!v1Symbols.has(normalizedSymbol)) throw new Error("Manual CSV import is limited to the seven v1 symbols.");
  if (interval !== "1day") throw new Error("V1 manual CSV import accepts daily candles only.");

  const candles = parseCsvCandles(csv);
  if (!candles.length) throw new Error("CSV must include Date, Open, High, Low, and Close columns.");
  const asOf = candles.at(-1)!.t;
  const payload = {
    symbol: normalizedSymbol,
    provider: "Manual CSV",
    meta: {
      source: "Manual CSV",
      asOf,
      isDelayed: true,
      isComplete: true,
      interval
    },
    candles,
    warning: "Manual CSV data is a fallback. Verify the file source and timestamp before acting."
  };

  if (!supabase) {
    localStorage.setItem(`${MANUAL_CSV_PREFIX}${normalizedSymbol}.${interval}`, JSON.stringify(payload));
    return { symbol: normalizedSymbol, candlesImported: candles.length, asOf, storage: "local-demo" as const };
  }

  const user = await supabase.auth.getUser();
  const userId = user.data.user?.id;
  if (!userId) throw new Error("Sign in before importing CSV data.");
  const { error } = await supabase.from("market_data_cache").insert({
    user_id: userId,
    symbol: normalizedSymbol,
    provider: "Manual CSV",
    timeframe: interval,
    freshness: "historical",
    data_timestamp: asOf,
    payload
  });
  if (error) throw error;
  return { symbol: normalizedSymbol, candlesImported: candles.length, asOf, storage: "supabase" as const };
}

export function isV1WatchSymbol(item: WatchSymbol): boolean {
  return v1Symbols.has(item.symbol.toUpperCase()) && ["stock", "ETF"].includes(item.assetType);
}

function readLocalWatchlist(): WatchSymbol[] {
  const stored = localStorage.getItem(WATCHLIST_KEY);
  if (!stored) return mockWatchlist;
  try {
    const parsed = JSON.parse(stored) as WatchSymbol[];
    const filtered = parsed.filter(isV1WatchSymbol);
    return filtered.length ? filtered : mockWatchlist;
  } catch {
    return mockWatchlist;
  }
}

function writeLocalWatchlist(items: WatchSymbol[]) {
  localStorage.setItem(WATCHLIST_KEY, JSON.stringify(items.filter(isV1WatchSymbol)));
}

function upsertWatchSymbol(items: WatchSymbol[], item: WatchSymbol) {
  const existing = items.findIndex((candidate) => candidate.symbol === item.symbol);
  if (existing < 0) return [...items, item];
  return items.map((candidate, index) => (index === existing ? item : candidate));
}

function mergeSavedWatchlist(savedItems: WatchSymbol[]) {
  const savedBySymbol = new Map(savedItems.map((item) => [item.symbol.toUpperCase(), item]));
  return mockWatchlist.map((defaultItem) => savedBySymbol.get(defaultItem.symbol) ?? defaultItem);
}

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function watchlistFromRow(row: WatchlistRow): WatchSymbol {
  return {
    id: row.id,
    symbol: row.symbol.toUpperCase(),
    assetType: row.asset_type,
    timeframe: row.timeframe,
    enabled: row.enabled,
    notificationsEnabled: row.notifications_enabled
  };
}
